import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useUser } from '../../../contexts/UserContext';
import {
  tlsrptApi,
  fmtEpoch,
  fmtNumber,
  TlsRptReport,
} from '../../../services/tlsrptApi';

const TlsRptReportDetail: React.FC = () => {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const { domain: domainParam, reportUid: uidParam } = useParams<{
    domain: string;
    reportUid: string;
  }>();
  const domain = domainParam ? decodeURIComponent(domainParam) : '';
  const reportUid = uidParam ? decodeURIComponent(uidParam) : '';

  const [report, setReport] = useState<TlsRptReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin()) navigate('/app/welcome');
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin() || !domain || !reportUid) return;
    tlsrptApi
      .getReport(domain, reportUid)
      .then(setReport)
      .catch(e => setError(e instanceof Error ? e.message : 'load failed'));
  }, [domain, reportUid, isAdmin]);

  if (!isAdmin()) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Button
          component={RouterLink}
          to={`/app/admin/tlsrpt/domains/${encodeURIComponent(domain)}`}
          startIcon={<ArrowBackIcon />}
          size="small"
        >
          Back to {domain}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load: {error}</Alert>}
      {!report && !error && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      )}

      {report && (
        <>
          <Box mb={3}>
            <Typography variant="h5" fontWeight={600}>
              {report.organization || '(unknown reporter)'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Report <code>{report.report_id}</code> for <strong>{report.domain}</strong>
              {' · '}
              window {fmtEpoch(report.date_begin)} → {fmtEpoch(report.date_end)}
              {report.contact_info && (
                <>
                  {' · '}contact <code>{report.contact_info}</code>
                </>
              )}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} mb={3}>
            <Chip
              label={`${fmtNumber(report.total_successful)} successful sessions`}
              color={report.total_successful > 0 ? 'success' : 'default'}
              variant="outlined"
            />
            <Chip
              label={`${fmtNumber(report.total_failure)} failure sessions`}
              color={report.total_failure > 0 ? 'error' : 'default'}
              variant="outlined"
            />
          </Stack>

          {(report.policies || []).map((policy, idx) => (
            <Card key={idx} sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                  <Box>
                    <Typography variant="h6">Policy {idx + 1}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      type=<code>{policy.policy_type || '(none)'}</code> · domain=<code>{policy.policy_domain || '—'}</code>
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      size="small"
                      label={`${fmtNumber(policy.successful_count)} success`}
                      color={policy.successful_count > 0 ? 'success' : 'default'}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`${fmtNumber(policy.failure_count)} failure`}
                      color={policy.failure_count > 0 ? 'error' : 'default'}
                      variant="outlined"
                    />
                  </Stack>
                </Stack>

                {policy.mx_hosts && policy.mx_hosts.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="caption" color="text.secondary">MX hosts</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {policy.mx_hosts.map(mx => (
                        <Chip key={mx} size="small" variant="outlined" label={mx} sx={{ fontFamily: 'monospace' }} />
                      ))}
                    </Box>
                  </Box>
                )}

                {policy.policy_string && policy.policy_string.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="caption" color="text.secondary">Policy text (as the reporter saw it)</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', mt: 0.5 }}>
                      {policy.policy_string.join('\n')}
                    </Paper>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" mb={1}>Failures</Typography>
                {(!policy.failures || policy.failures.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    No failure details for this policy block.
                  </Typography>
                )}
                {policy.failures && policy.failures.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Result type</TableCell>
                          <TableCell>Sending MTA IP</TableCell>
                          <TableCell>Receiving MX</TableCell>
                          <TableCell>Receiving IP</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell>Additional info</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {policy.failures.map((f, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.result_type}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.sending_mta_ip}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.receiving_mx_hostname}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.receiving_ip}</TableCell>
                            <TableCell align="right">{fmtNumber(f.failed_session_count)}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                              {f.additional_information || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          ))}

          {report.raw_s3_key && (
            <Typography variant="caption" color="text.secondary">
              Raw report at <code>s3://travelminds-ses-inbound/{report.raw_s3_key}</code>
              {report.processed_at && <> · ingested {report.processed_at}</>}
            </Typography>
          )}
        </>
      )}
    </Container>
  );
};

export default TlsRptReportDetail;
